import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Sidebar from '../components/Sidebar';
import { AuthProvider } from '../context/AuthContext';
import ActionBar from '../components/ActionBar';
import useScreenWidth from '../hooks/useScreenWidth';
import { useLoading } from '../context/LoadingContext';
import Loading from '../components/Loading';
import { useError } from '../context/ErrorContext';
import ErrorModal from '../components/modal/ErrorModal';
import Footer from '../components/Footer';
import WhatsAppFloatingButton from '../components/WhatsAppFloatingButton';
import useAuth from '../hooks/useAuth';
import SchoolYearGate from '../components/school/SchoolYearGate';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import sidebarLinksData from '../data/sidebarLinks.json';

type ReservedArea = 'family' | 'teacher' | 'admin';
const AREA_TITLE_KEYS: Record<ReservedArea, string> = {
  family: 'familyAreaTitle',
  teacher: 'teacherAreaTitle',
  admin: 'shell.adminArea',
};

// Titolo della scheda nelle aree riservate: "Pagina · Area · Formando PerCorsi".
// Le pagine pubbliche lo impostano da sole con il componente SEO.
const ReservedAreaTitle = ({ pathname }: { pathname: string }) => {
  const { t } = useTranslation();
  const area = pathname.split('/')[1] as ReservedArea;
  if (!(area in AREA_TITLE_KEYS)) return null;
  const links = [...sidebarLinksData[area], { name: 'esercizi', subPath: `/${area}/esercizi` }];
  const page = links.find((link) => link.subPath.toLowerCase() === pathname.toLowerCase().replace(/\/$/, ''));
  const parts = [page ? t(`sidebar.${page.name}`) : null, t(AREA_TITLE_KEYS[area]), 'Formando PerCorsi'];
  return (
    <Helmet>
      <title>{parts.filter(Boolean).join(' · ')}</title>
    </Helmet>
  );
};

const PUBLIC_REDIRECT_PATHS = ['/', '/chi-siamo', '/lavora-con-noi', '/teacher-application', '/login', '/forgot-password', '/admin/login'];
const MAINTENANCE_MODE = process.env.REACT_APP_MAINTENANCE_MODE === 'true';

const PublicAuthRedirect = () => {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (MAINTENANCE_MODE) return;
    if (!PUBLIC_REDIRECT_PATHS.includes(location.pathname)) return;
    if (auth.isAuth !== true || !auth.token) return;
    if (sessionStorage.getItem('googleNewUser') === 'true') return;

    if (auth.isAdmin) navigate('/admin/home', { replace: true });
    else if (auth.isTeacher) navigate('/teacher/home', { replace: true });
    else if (auth.isFamily) navigate('/family/home', { replace: true });
  }, [auth.isAuth, auth.token, location.pathname]);

  return null;
};

interface MainContentProps {
  Page: any;
}

function MainContent({ Page }: MainContentProps) {
  const location = useLocation();
  const [showDescription, setShowDescription] = useState(true);
  const [showNavbar, setShowNavbar] = useState(true);
  const [showFooter, setShowFooter] = useState(!['family', 'teacher', 'login', 'register','teacher-application'].includes(location.pathname.split('/')[1]));
  const [simplifiedNavbar, setSimplifiedNavbar] = useState(location.pathname.split('/')[1] === 'teacher-application');
  const [registrationStep, setRegistrationStep] = useState(0);
  const screenWidth = useScreenWidth();
  const { loading } = useLoading();
  const { error } = useError();

  useEffect(() => {
    setShowDescription(['/login', '/lavora-con-noi', '/chi-siamo', '/'].includes(location.pathname));
    setShowNavbar(!['family', 'teacher', 'admin'].includes(location.pathname.split('/')[1]) || location.pathname === '/admin/login');
    setShowFooter(!['family', 'teacher', 'login', 'register','admin','teacher-application'].includes(location.pathname.split('/')[1]));
    setSimplifiedNavbar(location.pathname.split('/')[1] === 'teacher-application');

    if (location.pathname.includes('register')) {
      const stored = localStorage.getItem('registrationForm');
      if (stored) {
        const parsed = JSON.parse(stored);
        setRegistrationStep(parsed.currentStep ?? 0);
      }
    } else {
      setRegistrationStep(0);
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleStepChange = (e: Event) => {
      setRegistrationStep((e as CustomEvent).detail);
    };
    window.addEventListener('registrationStepChange', handleStepChange);
    return () => window.removeEventListener('registrationStepChange', handleStepChange);
  }, []);

  const isMobile = screenWidth < 640;
  const isTablet = screenWidth >= 640 && screenWidth < 1024;
  const isPublicPage = !['family', 'teacher', 'admin'].includes(location.pathname.split('/')[1]);
  return (
    <>
      <AuthProvider>
        <PublicAuthRedirect />
        {!showNavbar && <ReservedAreaTitle pathname={location.pathname} />}
        {showNavbar && <Navbar descriptionVisible={showDescription} simplified={simplifiedNavbar} hideLoginPrompt={registrationStep > 0 || location.pathname === '/admin/login'} />}
        <div
          className={`${!showNavbar && 'min-h-screen lg:h-screen bg-fpcpink w-full flex'} pb-[72px] lg:pb-[0px] ${
            showNavbar && 'flex justify-center'
          } z-20`}>
          {!showNavbar && !isMobile && !isTablet && !location.pathname.includes('/admin/login') && <Sidebar pathname={location.pathname} />}
          {loading && <Loading />}
          {error.error && <ErrorModal />}
          {/* La pagina di conferma apre già la sua modale: qui il gate resta spento. */}
          {location.pathname.split('/')[1] === 'family' &&
            !location.pathname.startsWith('/family/school-year/confirm') && <SchoolYearGate />}
          {/* Le pagine pubbliche hanno già il loro <main>; l'area riservata lo riceve da qui */}
          {showNavbar ? (
            <div className="w-full flex justify-center">
              <Page />
            </div>
          ) : (
            <main className="p-2 md:p-4 w-full flex justify-center">
              <Page />
            </main>
          )}
        </div>
        {showFooter && <Footer />}
        {isPublicPage && <WhatsAppFloatingButton />}
        {!showNavbar && (isMobile || isTablet) && <ActionBar pathname={location.pathname} />}
      </AuthProvider>
    </>
  );
}

export default MainContent;
